/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Radio, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { recordsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import { allFields, formatValue } from './fieldValues'
import { usePersonLabel } from './PersonInput'

const empty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)

/**
 * Merge a duplicate into this record: per field where both have a value, choose which to keep;
 * empty fields here are filled from the duplicate. Its notes, history, tasks, emails, files and
 * links move here, and the duplicate is deleted.
 */
const MergeDialog = ({ record, form, duplicateId, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const personLabel = usePersonLabel()
  const [useSource, setUseSource] = useState([])
  const { data: dup, isLoading, error } = useQuery({ queryKey: [tenantId, 'records', 'one', duplicateId], queryFn: () => recordsAPI.get(duplicateId) })
  const merge = useMutation({
    mutationFn: () => recordsAPI.merge(record.id, duplicateId, useSource),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
      queryClient.invalidateQueries({ queryKey: [tenantId, 'tasks'] })
      notify(`Merged: ${r.activities} history entries, ${r.tasks} task${r.tasks === 1 ? '' : 's'}, ${r.files} file${r.files === 1 ? '' : 's'} and ${r.links} link${r.links === 1 ? '' : 's'} moved here`)
      onClose()
    },
  })
  const show = (f, value, lookups) => {
    if (empty(value)) return <Typography variant="body2" color="text.disabled">empty</Typography>
    if (f.fieldType === 'USER') return personLabel(value, lookups?.[f.fieldKey])
    if (f.fieldType === 'LOOKUP') return lookups?.[f.fieldKey]?.title || value
    return formatValue(f, value)
  }
  const rows = dup ? allFields(form).filter((f) => f.fieldType !== 'ROLLUP' && !empty(dup.data[f.fieldKey])) : []
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Merge “{dup?.title || '…'}” into this record</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Choose which value to keep where both have one. Empty fields here are filled from the duplicate. Its notes, history, tasks, emails, files and links
          move here, and the duplicate is deleted.
        </DialogContentText>
        {(error || merge.error) && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(error || merge.error)}</Alert>}
        {isLoading && <Typography variant="body2">Loading…</Typography>}
        {dup && (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Field</TableCell><TableCell>This record</TableCell><TableCell>Duplicate</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={3}>The duplicate has no values to bring over.</TableCell></TableRow>}
                {rows.map((f) => {
                  const mine = record.data[f.fieldKey]
                  const both = !empty(mine)
                  const fromDup = !both || useSource.includes(f.fieldKey)
                  const pick = (source) => setUseSource((list) => (source ? [...list, f.fieldKey] : list.filter((k) => k !== f.fieldKey)))
                  return (
                    <TableRow key={f.fieldKey}>
                      <TableCell sx={{ fontWeight: 500 }}>{f.label}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {both && <Radio size="small" checked={!fromDup} onChange={() => pick(false)} inputProps={{ 'aria-label': `Keep this ${f.label}` }} />}
                          {show(f, mine, record.lookups)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {both ? <Radio size="small" checked={fromDup} onChange={() => pick(true)} inputProps={{ 'aria-label': `Use the duplicate's ${f.label}` }} />
                            : <Typography variant="caption" color="success.main" sx={{ mr: 1 }}>fills in</Typography>}
                          {show(f, dup.data[f.fieldKey], dup.lookups)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={!dup || merge.isPending} onClick={() => merge.mutate()}>
          {merge.isPending ? 'Merging…' : 'Merge and delete the duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MergeDialog
